import React, {useEffect, useRef, useState} from "react";
import clsx from "clsx";
import {SelectedColor, SelectedColors} from "data/models/survey/colors";
import {Button, Label, Radio} from "flowbite-react";
import {capitalize} from "helpers/capitalize";
import {useRecommendedColors2} from "data/access/survey-question-pages/colors2";
import {calculateBackgroundColor, calculateColorLight} from "helpers/color-calculator";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faX} from "@fortawesome/free-solid-svg-icons";
import "./index.css";
import {traitName} from "data/models/shared/bc-sheet/brand-trait";
import LoadingSurvey from "components/Survey/LoadingSurvey";
import {assertNoError, assertNotNullOrUndefined} from "helpers/asserts";

export type ColorsSelect_SurveyQuestionPage_Props = {
  visible: boolean;
  questionId: string;
  value: SelectedColors | undefined;
  onChange: (id: string, value: SelectedColors) => void;
}

export type PartiallySelectedColors = {
  primary?: SelectedColor;
  secondary?: SelectedColor;
  accent?: SelectedColor;
}

type PartiallySelectedColorsKey = keyof PartiallySelectedColors;

type ColorToSelectProps = {
  index: number;
  colorToSelect: PartiallySelectedColorsKey;
  width: string;
  height: string;
}

export default function Colors2SelectSurveyQuestionPage(
    {
      visible,
      questionId,
      value,
      onChange
    }: ColorsSelect_SurveyQuestionPage_Props
) {
  const swatchRef = useRef<null | HTMLDivElement>(null)
  const [colorChoices, colorChoicesLoading, colorChoicesError] = useRecommendedColors2(); // RecommendedColors2[]

  const [pickingColor, setPickingColor] = useState<PartiallySelectedColorsKey>("primary");
  const [partiallySelectedColor, setPartiallySelectedColor] = useState<PartiallySelectedColors>({});
  const [selectedColorGroup, setSelectedColorGroup] = useState<string[] | null>(null);
  const [openedColorsGroup, setOpenedColorsGroup] = useState(1);
  const [showAnimation, setShowAnimation] = useState(true);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowAnimation(false);
    }, 6000);
    return () => clearInterval(timeoutId);
  }, [])

  const selectedAllPartialColors = PARTIAL_COLOR_KEYS.every(key => key in partiallySelectedColor);

  function changeSelectedColor(colorSelection: string) {
    const [trait, color] = colorSelection.split(".");
    const newlySelectedColor = {
      ...partiallySelectedColor,
      [pickingColor]: {trait, color,},
    };
    setPartiallySelectedColor(newlySelectedColor);
  }

  function selectPartialColors() {
    if (!selectedAllPartialColors) {
      return;
    }
    assertNotNullOrUndefined(partiallySelectedColor.primary);
    onChange(questionId, {
      ...partiallySelectedColor,
      background: {
        trait: partiallySelectedColor.primary.trait,
        color: calculateBackgroundColor(partiallySelectedColor.primary.color),
      },
    } as SelectedColors);
    setPartiallySelectedColor({});
    setSelectedColorGroup(null);
  }

  function cancelPartialColors() {
    setPartiallySelectedColor({});
    setSelectedColorGroup(null);
  }

  function handleColorPaletteChange(color: string) {
    changeSelectedColor(`custom.${color}`);

    setPickingColor(PARTIAL_COLOR_KEYS[(PARTIAL_COLOR_KEYS.indexOf(pickingColor) + 1) % PARTIAL_COLOR_KEYS.length]);
  }

  function changePickingColor(changedValue: PartiallySelectedColorsKey) {
    setPickingColor(changedValue);
  }

  function ColorToSelect({index, colorToSelect, width, height}: ColorToSelectProps) {
    const selectedColorHex = selectedColorGroup !== null
        ? partiallySelectedColor?.[colorToSelect]?.color
        : value?.[colorToSelect].color;
    return <div className={clsx(width, "flex flex-col items-center")}>
      <div className={"w-full text-left ml-1"}>
      <span
          className={"text-neutral-500 text-sm font-bold"}
      >{capitalize(colorToSelect)}</span>
      </div>
      <Radio
          className="hidden peer"
          id={`${questionId}.${colorToSelect}`}
          value={colorToSelect}
          checked={selectedColorGroup !== null && pickingColor === colorToSelect}
          onChange={({target: {value: changedValue}}) => changePickingColor(changedValue as PartiallySelectedColorsKey)}
      />
      <Label
          htmlFor={`${questionId}.${colorToSelect}`}
          className={clsx(
              "w-full inline-flex text-neutral-500 border-3 rounded-md peer-checked:border-neutral-500 peer-checked:text-bc-blue hover:text-neutral-500",
              height,
              selectedColorGroup !== null && "border-neutral-50 cursor-pointer" || "border-transparent",
          )}
          style={{
            ...((showAnimation && value === undefined) ? {
              animationName: `color-highlight`,
              animationDuration: "2s",
              animationDelay: `${index * 1.5}s`,
            } : {}),
          }}
      >
        <div
            className={"grow m-1 rounded-md"}
            style={{
              backgroundColor: selectedColorHex ?? "transparent",
            }}></div>
      </Label>
      <div
          className={"mx-4 mb-2 w-full text-neutral-500 text-sm text-left"}
      >
        {selectedColorHex?.toUpperCase() ?? ""}&nbsp;
      </div>
    </div>
  }

  if (colorChoicesLoading) {
    return visible && <LoadingSurvey/> || null;
  }
  assertNoError(colorChoicesError);

  function handleSelectColorGroup(colorGroupToChoose: string[]) {
    setSelectedColorGroup(colorGroupToChoose);
    setPartiallySelectedColor({});
    swatchRef?.current?.scrollIntoView({behavior: "smooth"})
  }

  function handleShowMore() {
    if (openedColorsGroup < (colorChoices?.length ?? 0)) {
      setOpenedColorsGroup(openedColorsGroup + 1);
    }
  }

  return <div className={"flex flex-col w-full"}>
    <div className={"flex flex-col w-full mb-8"}>
      <div className={"my-4 text-center"}>
        <span className={"text-3xl font-bold"}>Choose your brand colors</span>
      </div>
      <div className={"mb-4 text-center"}>
      <span className={"text-xl text-neutral-500"}>
        Your primary color is the most dominant color for your brand.
        Your secondary color should complement it.
        The accent color should be dark or bright.<br/><br/>
        Select a color group, then select each color from the group.
      </span>
      </div>
      <div className={"w-full max-w-3xl mx-auto border-0 p-4"} ref={swatchRef}>
        <fieldset name={questionId} className="flex flex-row gap-4 justify-center" autoFocus={true}>
          <ColorToSelect index={0} colorToSelect={"primary"} width={"w-1/2"} height={"min-h-32"}/>
          <ColorToSelect index={1} colorToSelect={"secondary"} width={"w-1/4"} height={"min-h-32"}/>
          <ColorToSelect index={2} colorToSelect={"accent"} width={"w-1/4"} height={"min-h-12"}/>
        </fieldset>
      </div>
    </div>
    <div>
      <div className={"relative"}>
        {selectedColorGroup !== null && <div
            className={"absolute -inset-y-10 flex flex-row justify-center w-full h-full bg-white/90"}
        >
            <div className={"flex flex-col bg-white pb-8 px-8"}>
                <div className={"flex flex-row-reverse mb-2"}>
                    <Button
                        color={"blue"}
                        onClick={() => cancelPartialColors()}
                    >
                        <FontAwesomeIcon icon={faX} color={"white"} size={"sm"}/>
                    </Button>
                </div>
                <div
                    className={"grid grid-cols-2 p-4 border-3 border-neutral-500 rounded-2xl justify-center"}
                >
                  {selectedColorGroup.map((color, index) => <div
                      key={index}
                      className={"flex flex-col w-36"}
                  >
                    <div
                        className={clsx(
                            "h-20 flex flex-col border items-center mt-2 cursor-pointer",
                            (index % 2 === 0) && "mr-4",
                        )}
                        style={{
                          borderColor: calculateColorLight(color) < 240
                              ? color : "#f0f0f0",
                          backgroundColor: color,
                        }}
                        onClick={() => handleColorPaletteChange(color)}
                    >
                    </div>
                    <div
                        key={index}
                        className={"text-neutral-500 text-sm text-left"}
                    >
                      {color}
                    </div>
                  </div>)}
                </div>
                <div className={"flex justify-center mt-4"}>
                    <Button
                        color={"blue"}
                        disabled={!selectedAllPartialColors}
                        onClick={() => selectPartialColors()}
                    >Select</Button>
                </div>
            </div>
        </div>}
        <div className={"flex flex-col"}>
          <div className={"mt-4 text-center"}>
            <span className={"text-3xl font-bold"}>Based on your brand traits, we recommend the following colors.</span>
          </div>
          <div className="w-full flex flex-col mt-8 gap-12">
            {colorChoices?.slice(0, openedColorsGroup).map((colorChoice, index) =>
                <div key={index} className={"flex flex-col"}>
                  {colorChoice.traitKey !== 'both' && <div className={"text-center"}>
                <span
                    className={"text-neutral-500 font-bold"}>These colors emphasize: {traitName(colorChoice.traitKey)}</span>
                  </div>}
                  {colorChoice.traitKey === 'both' && <div className={"text-center"}>
                      <span className={"text-neutral-500 font-bold"}>These colors balance all your traits</span>
                  </div>}
                  <div className={"mt-4 flex flex-wrap gap-4 xl:max-h-72 justify-center"}>
                    {colorChoice.colors.map((colorGroupToChoose, index) =>
                        <div
                            key={index}
                            className={"grid grid-cols-2 p-4 border-3 hover:border-neutral-500 border-neutral-50 rounded-2xl justify-center cursor-pointer"}
                            onClick={() => handleSelectColorGroup(colorGroupToChoose)}
                        >
                          {colorGroupToChoose.map((color, index) => <div
                              key={index}
                              className={"flex flex-col w-36"}
                          >
                            <div

                                className={clsx(
                                    "h-20 flex flex-col border items-center mt-2",
                                    (index % 2 === 0) && "mr-4",
                                )}
                                style={{
                                  borderColor: calculateColorLight(color) < 240
                                      ? color : "#f0f0f0",
                                  backgroundColor: color,
                                }}
                            >
                            </div>
                            <div
                                key={index}
                                className={"text-neutral-500 text-sm text-left"}
                            >
                              {color}
                            </div>
                          </div>)}
                        </div>
                    )}
                  </div>
                </div>
            )}
          </div>
          {
              openedColorsGroup < (colorChoices?.length ?? 0) &&
              <div className={"mt-12 w-fit mx-auto"}>
                  <Button
                      className={"px-12 rounded-lg"}
                      color={"gray"}
                      onClick={() => handleShowMore()}
                  >
                      <div className={"flex flex-col"}>
                          <div><span className={"text-neutral-500 font-bold"}>Show more</span></div>
                          <div><FontAwesomeIcon icon={faChevronDown} size={"2xl"}/></div>
                      </div>
                  </Button>
              </div>
          }
        </div>
      </div>
    </div>
  </div>
}

const PARTIAL_COLOR_KEYS: PartiallySelectedColorsKey[] = ["primary", "secondary", "accent"];
